"use client";

import { useEffect, useMemo, useState } from "react";

type Question = {
  section: string;
  question: string;
  options: string[];
  answer: number;
  insight: string;
};

const questions: Question[] = [
  { section: "回應慈悲", question: "羅馬書十二章 1 節的「所以」，提醒信徒以甚麼作為獻上生命的起點？", options: ["努力證明自己比別人好", "回應神已經賜下的慈悲", "換取神更多祝福"], answer: 1, insight: "獻上不是交易，而是對神救贖與慈悲的感恩回應。" },
  { section: "辨認塑造", question: "「不要效法這個世界」在講道中最接近哪個意思？", options: ["完全離開社會與人群", "只要避免明顯的壞行為", "不要在不知不覺間被世界的價值觀塑造"], answer: 2, insight: "外貌、金錢、地位與比較，常在我們未察覺時定義了人的價值。" },
  { section: "察驗內心", question: "當我因外貌、年齡、財富或地位而覺得自己沒有價值，最合適的第一步是甚麼？", options: ["更努力追上世界的標準", "辨認這些標準，讓神的真理重新塑造我", "把不安隱藏起來"], answer: 1, insight: "出路不是追得更快，而是先看清：究竟是誰在決定我的價值。" },
  { section: "心意更新", question: "講道指出「心意更新而變化」主要強調甚麼？", options: ["只靠意志令自己變好", "神在我們裡面工作，使心意被更新", "一次聚會便能完成所有改變"], answer: 1, insight: "更新既是神的工作，也邀請我們持續讓真理進入思想與生活。" },
  { section: "操練真理", question: "為甚麼講道以「一種腿法練一萬次」來形容讀神的話？", options: ["經文越難越有價值", "真正的力量來自持續、反覆的操練", "只需要熟記一節經文"], answer: 1, insight: "世界長期塑造我們，真理也需要持續被思想、相信和實踐。" },
  { section: "新的身分", question: "「若有人在基督裡，他就是新造的人」帶來甚麼盼望？", options: ["過去的經歷從此從記憶消失", "我不必永遠被過去的自卑定義", "信主後不再有任何限制"], answer: 1, insight: "過去未必能改寫，但我們可以在基督裡用新的眼光認識自己。" },
  { section: "神的眼光", question: "《創世記》說神看祂所造的「甚好」，對我們的價值有何提醒？", options: ["只有能力出眾的人才美好", "人的價值取決於別人的讚好", "我們是神所造、看為美好並有價值的人"], answer: 2, insight: "真正決定價值的，是創造和救贖我們的主，不是世界的標價。" },
  { section: "合乎中道", question: "羅馬書十二章 3 節所說「看得合乎中道」是指甚麼？", options: ["不高估也不低估，按神的恩典真實看自己", "永遠不要提及自己的長處", "只與比自己弱的人比較"], answer: 0, insight: "謙卑不是自我否定，而是誠實地承認限制，也肯定神所賜的恩典。" },
  { section: "彼此配搭", question: "不同恩賜和服侍崗位，代表甚麼？", options: ["台前服侍的人較有價值", "信心較大的人可以輕看別人", "功用雖不同，每個肢體同樣重要"], answer: 2, insight: "帶領、思考、行動或幕後服侍，都是神所賜、彼此需要的。" },
  { section: "生命回應", question: "按照講道結語，健康地欣賞自己最終會帶來甚麼？", options: ["只專注自己的感受", "更自由地愛人、服侍人並榮耀神", "不再需要別人的提醒"], answer: 1, insight: "知道自己蒙愛，不是停在自己身上，而是讓愛流向別人。" },
];

function loadProgress() {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem("sermon-review-progress-v1") ?? "{}") as {
      screen?: "welcome" | "quiz" | "result";
      current?: number;
      answers?: (number | null)[];
    };
  } catch {
    window.localStorage.removeItem("sermon-review-progress-v1");
    return {};
  }
}

function resultCopy(score: number) {
  if (score >= 90) return { title: "扎根真理，繼續活出愛", text: "你掌握了信息的核心。這星期選一段最容易被世界塑造的地方，用一句經文反覆更新自己。" };
  if (score >= 70) return { title: "正在更新，給自己成長空間", text: "你已看見神眼光與世界標準的分別。重溫答錯的題目，並寫下一件神正在你生命中改變的事。" };
  if (score >= 50) return { title: "重新對焦，聽見神怎樣看你", text: "先不要用分數否定自己。慢慢重讀羅馬書十二章 1–3 節，辨認一個常令你覺得「不夠好」的聲音。" };
  return { title: "由被愛開始，不由表現開始", text: "這不是一張價值成績表。邀請你重新聆聽信息，並找一位同行者分享：我最難接納自己的地方是甚麼？" };
}

export default function Home() {
  const [progress] = useState(loadProgress);
  const [screen, setScreen] = useState<"welcome" | "quiz" | "result">(progress.screen ?? "welcome");
  const [current, setCurrent] = useState(Math.min(9, Math.max(0, progress.current ?? 0)));
  const [answers, setAnswers] = useState<(number | null)[]>(progress.answers?.length === 10 ? progress.answers : Array(10).fill(null));
  const score = useMemo(() => answers.reduce<number>((total, answer, i) => total + (answer === questions[i].answer ? 10 : 0), 0), [answers]);
  const selected = answers[current];
  const q = questions[current];

  useEffect(() => {
    window.localStorage.setItem("sermon-review-progress-v1", JSON.stringify({ screen, current, answers }));
  }, [screen, current, answers]);

  const restart = () => {
    window.localStorage.removeItem("sermon-review-progress-v1");
    setAnswers(Array(10).fill(null)); setCurrent(0); setScreen("welcome");
  };

  if (screen === "welcome") return (
    <main className="shell welcome">
      <div className="glow glow-a" /><div className="glow glow-b" />
      <section className="hero">
        <p className="eyebrow">恩景堂 · 講道溫習</p>
        <div className="verse-mark">羅<br />12</div>
        <h1>誰來愛我？</h1>
        <p className="scripture">「不要效法這個世界，只要心意更新而變化。」</p>
        <p className="intro">用 10 個問題，重新察看世界如何塑造我、神如何喜悅我，以及我如何學習欣賞自己。</p>
        <div className="facts"><span>10 條選擇題</span><span>每題 10 分</span><span>滿分 100</span></div>
        <button className="primary" onClick={() => setScreen("quiz")}>開始溫習 <span>→</span></button>
        <p className="gentle">分數幫助回顧，不能衡量你在神眼中的價值。</p>
      </section>
    </main>
  );

  if (screen === "quiz") return (
    <main className="shell quiz-shell">
      <header className="topbar"><button className="brand" onClick={restart}>誰來愛我？</button><span>{current + 1} / 10</span></header>
      <div className="progress"><i style={{ width: `${(current + 1) * 10}%` }} /></div>
      <section className="question-card">
        <div className="q-meta"><span>第 {String(current + 1).padStart(2, "0")} 題</span><span>{q.section}</span><span>10 分</span></div>
        <h2>{q.question}</h2>
        <div className="options">
          {q.options.map((option, i) => <button key={option} className={selected === i ? "option selected" : "option"} onClick={() => setAnswers(a => a.map((v, index) => index === current ? i : v))}><b>{String.fromCharCode(65 + i)}</b><span>{option}</span><em>✓</em></button>)}
        </div>
        <div className="nav-row">
          <button className="secondary" disabled={current === 0} onClick={() => setCurrent(c => c - 1)}>← 上一題</button>
          <button className="primary compact" disabled={selected === null} onClick={() => current === 9 ? setScreen("result") : setCurrent(c => c + 1)}>{current === 9 ? "查看結果" : "下一題 →"}</button>
        </div>
      </section>
      <p className="footnote">羅馬書十二章 1–3 節 · 何敬賢傳道</p>
    </main>
  );

  const result = resultCopy(score);
  return (
    <main className="shell result-shell">
      <section className="result-hero">
        <p className="eyebrow">溫習完成</p>
        <div className="score-ring"><strong>{score}</strong><span>/ 100</span></div>
        <h1>{result.title}</h1><p>{result.text}</p>
        <div className="result-actions"><button className="primary compact" onClick={() => { setCurrent(0); setScreen("quiz"); }}>逐題重溫</button><button className="secondary" onClick={restart}>重新開始</button></div>
      </section>
      <section className="reflection">
        <p className="eyebrow">帶走一個問題</p><h2>今天，我最需要用神的眼光<br />重新看自己的哪一部分？</h2>
        <div className="three-lines"><span>我被世界塑造</span><span>我是神所喜悅的</span><span>我可以欣賞自己</span></div>
      </section>
      <section className="review"><h2>逐題回顧</h2>{questions.map((item, i) => { const correct = answers[i] === item.answer; return <article key={item.question} className={correct ? "review-item correct" : "review-item wrong"}><span>{i + 1}</span><div><h3>{correct ? "答對了" : "再想一想"} · {item.section}</h3><p>{item.insight}</p>{!correct && <small>正確答案：{item.options[item.answer]}</small>}</div><b>{correct ? "+10" : "0"}</b></article>; })}</section>
    </main>
  );
}
